/**
 * Cart Recommendations
 *
 * CartRecommendations custom element — fetches product recommendations
 * via Shopify Product Recommendations API, renders carousel, handles
 * prev/next scrolling and AJAX add-to-cart for the recs carousel.
 */

// ── Helper: extract inner HTML of a selector from a raw HTML string ───────
function getSectionInnerHTML(html, selector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector(selector);
  return el ? el.innerHTML : null;
}

// ── Helper: re-render cart drawer sections after a cart mutation ─────────
async function refreshCartDrawer() {
  try {
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-count-bubble`);
    const state = await response.json();
    const drawerHtml = state['cart-drawer'];

    if (drawerHtml) {
      const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const selectors = [
        '.drawer__inner > .drawer__header',
        'cart-drawer-items',
        '.drawer__footer',
        '.drawer__inner-empty-wrapper',
        '.cart-progress-bar__message',
      ];

      selectors.forEach((sel) => {
        const liveEl = document.querySelector(sel);
        const sourceEl = drawerDoc.querySelector(sel);
        if (!liveEl || !sourceEl) return;

        liveEl.innerHTML = sourceEl.innerHTML;
      });

      // Re-bind overlay close (mirrors CartDrawer.renderContents)
      const drawer = document.querySelector('cart-drawer');
      const overlay = document.getElementById('CartDrawer-Overlay');
      if (drawer && overlay) {
        overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }

    const countBubble = document.getElementById('cart-count-bubble');
    if (countBubble && state['cart-count-bubble']) {
      const newHtml = getSectionInnerHTML(state['cart-count-bubble'], '.shopify-section');
      if (newHtml !== null) countBubble.innerHTML = newHtml;
    }

    // Notify other cart subscribers (Rebuy, progress bar, etc.)
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations' });
    }
  } catch (_err) {
    // Silent — drawer still shows current state
  }
}

// ── Helper: AJAX add-to-cart for a single variant ─────────────────────────
async function addVariantToCart(variantId, btn) {
  btn.classList.add('loading');
  btn.setAttribute('disabled', '');
  btn.querySelector('.loading__spinner')?.classList.remove('hidden');

  try {
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    });

    if (!addResponse.ok) {
      throw new Error(`Cart add failed: ${addResponse.status}`);
    }

    await refreshCartDrawer();

    // Ensure drawer stays open / re-opens
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.classList.contains('active')) {
      setTimeout(() => drawer.classList.add('animate', 'active'));
    }
  } catch (_err) {
    btn.classList.remove('loading');
    btn.removeAttribute('disabled');
    btn.querySelector('.loading__spinner')?.classList.add('hidden');
  }
}

// ── CartRecommendations custom element ───────────────────────────────────
if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.url = this.dataset.url || '/recommendations/products';
      this.sectionId = this.dataset.sectionId || 'cart-recommendations';
      this.limit = this.dataset.limit || 6;

      if (!this.productId) return;
      this.deferUntilDrawerOpen();
    }

    disconnectedCallback() {
      // Element is re-created on every cart-drawer re-render; clean up.
      this.drawerObserver?.disconnect();
      this.arrowsResizeObserver?.disconnect();
    }

    // Defer the recommendations fetch until the cart drawer is actually opened,
    // so it never fires on initial page load (CWV-safe). The hook is the `active`
    // class CartDrawer.open() adds to <cart-drawer>. IntersectionObserver is
    // unreliable here because <cart-drawer> is position:fixed full-viewport.
    deferUntilDrawerOpen() {
      const drawer = this.closest('cart-drawer');

      // No drawer ancestor, or drawer already open (e.g. re-rendered after an
      // AJAX add-to-cart while open) → load immediately.
      if (!drawer || drawer.classList.contains('active')) {
        this.fetchRecommendations();
        return;
      }

      this.drawerObserver = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          this.drawerObserver.disconnect();
          this.fetchRecommendations();
        }
      });
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    fetchRecommendations() {
      // Guard against a double fetch (e.g. observer + immediate paths racing).
      if (this.hasLoaded) return;
      this.hasLoaded = true;

      const url = `${this.url}?section_id=${this.sectionId}&product_id=${this.productId}&limit=${this.limit}`;

      fetch(url)
        .then((res) => res.text())
        .then((html) => {
          const inner = getSectionInnerHTML(html, '.shopify-section');
          if (!inner || !inner.trim()) return;

          this.innerHTML = inner;

          this.initCarousel();
          this.initAddToCart();
          this.refetchSingleSwatchCards();
        })
        .catch(() => {
          // Silently fail — cart drawer still works without recommendations
        });
    }

    initCarousel() {
      const track = this.querySelector('.cart-recs__track');
      const prevBtn = this.querySelector('.cart-recs__prev');
      const nextBtn = this.querySelector('.cart-recs__next');

      if (!track || !prevBtn || !nextBtn) return;

      const getScrollAmount = () => {
        const firstItem = track.querySelector('.cart-recs__item');
        if (!firstItem) return 220;
        const styles = getComputedStyle(track);
        const gap = parseInt(styles.columnGap || styles.gap || '8', 10);
        return firstItem.offsetWidth + gap;
      };

      prevBtn.addEventListener('click', () => track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' }));

      nextBtn.addEventListener('click', () => track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' }));

      const updateArrows = () => {
        const atEnd = track.scrollLeft + track.offsetWidth >= track.scrollWidth - 1;
        prevBtn.disabled = track.scrollLeft <= 0;
        nextBtn.disabled = atEnd;

        const wrapper = track.closest('.cart-recs__track-wrapper');
        if (wrapper) wrapper.dataset.atEnd = atEnd;
      };

      track.addEventListener('scroll', updateArrows, { passive: true });

      this.arrowsResizeObserver?.disconnect();
      this.arrowsResizeObserver = new ResizeObserver(() => updateArrows());
      this.arrowsResizeObserver.observe(track);
    }

    initAddToCart() {
      // Use event delegation so that ATC buttons still work after swatch-form.js
      // replaces the card wrapper (the old direct-bound handlers would be lost).
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.cart-recs__add-btn[data-variant-id]');
        if (btn) addVariantToCart(btn.dataset.variantId, btn);
      });
    }

    refetchSingleSwatchCards() {
      const storefrontToken = window.storefrontApiKey;
      if (!storefrontToken) return;

      const wrappers = this.querySelectorAll('adaptive-swatch-wrapper[data-hydrate-custom-swatches]');
      if (!wrappers.length) return;

      const targets = [];
      wrappers.forEach((wrapper) => {
        const cardWrapper = wrapper.closest('.cart-recs__card-wrapper');
        if (!cardWrapper) return;
        const form = wrapper.querySelector('form[data-product-url]');
        if (!form) return;
        const productUrl = form.dataset.productUrl;
        if (!productUrl) return;
        const handle = productUrl.split('?')[0].split('/products/')[1];
        if (!handle) return;
        targets.push({ handle, wrapper, cardWrapper });
      });

      if (!targets.length) return;

      const swatchFragment = `
        fields {
          key
          value
          reference {
            ... on MediaImage { image { url(transform: { maxWidth: 50 }) } }
            ... on Product { handle onlineStoreUrl }
            ... on ProductVariant { product { handle onlineStoreUrl } }
            ... on Metaobject {
              fields { key value reference { ... on MediaImage { image { url(transform: { maxWidth: 50 }) } } } }
            }
          }
        }
      `;
      const productFields = `
        swatchGroups: metafield(namespace: "custom", key: "custom_swatch_groups") {
          references(first: 5) {
            nodes {
              ... on Metaobject {
                fields {
                  key
                  value
                  references(first: 20) {
                    nodes { ... on Metaobject { ${swatchFragment} } }
                  }
                }
              }
            }
          }
        }
        colorSwatches: metafield(namespace: "custom", key: "custom_color_swatches") {
          references(first: 20) {
            nodes { ... on Metaobject { ${swatchFragment} } }
          }
        }
      `;

      const aliasedQueries = targets.map(
        (t, i) => `p${i}: product(handle: "${t.handle}") { ${productFields} }`
      );
      const query = `{ ${aliasedQueries.join('\n')} }`;

      fetch('/api/2025-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
        },
        body: JSON.stringify({ query }),
      })
        .then((res) => res.json())
        .then((json) => {
          if (!json.data) return;

          const sectionFallbackTargets = [];

          targets.forEach((target, i) => {
            const productData = json.data[`p${i}`];
            if (!productData) {
              sectionFallbackTargets.push(target);
              return;
            }

            let swatchNodes = [];

            if (productData.swatchGroups?.references?.nodes?.length) {
              productData.swatchGroups.references.nodes.forEach((group) => {
                const customSwatchesField = group.fields?.find((f) => f.key === 'custom_swatches');
                if (customSwatchesField?.references?.nodes) {
                  swatchNodes.push(...customSwatchesField.references.nodes);
                }
              });
            }

            if (!swatchNodes.length && productData.colorSwatches?.references?.nodes?.length) {
              swatchNodes = productData.colorSwatches.references.nodes;
            }

            if (!swatchNodes.length) return;

            const swatches = swatchNodes.map((metaobj) => {
              const fields = {};
              metaobj.fields.forEach((f) => { fields[f.key] = f; });

              let background = '';
              if (fields.image?.reference?.image?.url) {
                background = `url(${fields.image.reference.image.url})`;
              } else if (fields.colour?.reference) {
                const colourFields = {};
                fields.colour.reference.fields?.forEach((f) => { colourFields[f.key] = f; });
                if (colourFields.image?.reference?.image?.url) {
                  background = `url(${colourFields.image.reference.image.url})`;
                } else if (colourFields.color?.value) {
                  background = colourFields.color.value;
                }
              }

              let targetUrl = '';
              if (fields.linked_variant?.reference?.product?.onlineStoreUrl) {
                targetUrl = fields.linked_variant.reference.product.onlineStoreUrl;
              } else if (fields.linked_product?.reference?.onlineStoreUrl) {
                targetUrl = fields.linked_product.reference.onlineStoreUrl;
              }

              return { background, targetUrl, label: fields.label?.value || fields.title?.value || '' };
            });

            this.appendCustomSwatches(target.wrapper, swatches);
          });

          if (sectionFallbackTargets.length) {
            this.fetchSwatchesViaSection(sectionFallbackTargets);
          }
        })
        .catch(() => {});
    }

    fetchSwatchesViaSection(targets) {
      targets.forEach((target) => {
        fetch(`/products/${target.handle}?sections=cart-rec-swatch-data`)
          .then((res) => res.json())
          .then((json) => {
            const html = json['cart-rec-swatch-data'];
            if (!html) return;

            const jsonContent = html.replace(/<div[^>]*>/, '').replace(/<\/div>\s*$/, '').trim();

            let data;
            try {
              data = JSON.parse(jsonContent);
            } catch (e) {
              return;
            }

            if (!data.swatches || !data.swatches.length) return;

            const swatches = data.swatches.map((swatch) => ({
              background: swatch.image ? `url(${swatch.image})` : '',
              targetUrl: swatch.linked_product || '',
              label: swatch.label || '',
            }));

            this.appendCustomSwatches(target.wrapper, swatches);
          })
          .catch(() => {});
      });
    }

    // Append custom colour-swatch chips (sibling colourways) into a card's swatch list.
    // Built via DOM APIs (not innerHTML) so labels/URLs need no escaping. `swatches` is a
    // normalized array of { background, targetUrl, label }. Sets the true total on the
    // adaptive-swatch-wrapper and dispatches `swatch-count-updated` so its "+N" recalcs.
    appendCustomSwatches(wrapper, swatches) {
      const swatchList = wrapper.querySelector('.swatch-list');
      if (!swatchList || !swatches.length) return;

      const maxSwatches = parseInt(wrapper.dataset.maxDesktop || '5', 10);
      const nativeCount = swatchList.querySelectorAll(
        '.swatch-list__item:not(.swatch-list__item--custom)'
      ).length;
      const budget = maxSwatches - nativeCount;

      let rendered = 0;
      swatches.forEach((sw) => {
        if (rendered >= budget) return;

        // Only treat as a link when the URL is same-origin-relative or https.
        const isLink = typeof sw.targetUrl === 'string' && /^(\/|https:\/\/)/.test(sw.targetUrl);

        const li = document.createElement('li');
        li.className = 'swatch-list__item swatch-list__item--custom';

        const labelEl = document.createElement(isLink ? 'a' : 'span');
        labelEl.className = 'swatch-input__label';
        labelEl.title = sw.label || '';
        if (isLink) labelEl.setAttribute('href', sw.targetUrl);

        const swatchSpan = document.createElement('span');
        swatchSpan.className = `swatch swatch--square${sw.background ? '' : ' swatch--unavailable'}`;
        if (sw.background) swatchSpan.style.setProperty('--swatch--background', sw.background);

        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = sw.label || '';

        labelEl.append(swatchSpan, hidden);
        li.appendChild(labelEl);
        swatchList.appendChild(li);
        rendered++;
      });

      if (rendered > 0) {
        wrapper.dataset.totalSwatches = nativeCount + swatches.length;
        wrapper.dispatchEvent(new Event('swatch-count-updated'));
      }
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
}
